import { ReactNode, useMemo } from 'react';
import { Answers } from '../../components/Answers';
import { Countdown as CountdownBase } from '../../components/Countdown';
import { FloatingLogo } from '../../components/FloatingLogo';
import { useLanguage } from '../../context/LanguageContext';
import { AnswerType, QuestionType } from '../../types/entities';
import {
  Bottom,
  Container,
  CountdownWrapper,
  NextQuestion,
  ProgressLabel,
  QuestionText,
  QuestionsCompleted,
  RightColumn,
  SkipQuestionButton,
  TimerSlot,
  TopBar,
} from './styles';

export type QuestionLayoutMode = 'trivia' | 'survey';

type QuestionLayoutProps = {
  mode: QuestionLayoutMode;
  question?: QuestionType;
  currentIndex: number;
  questionCount: number;
  playerName: string;
  points: number;
  loadingNewQuestion: boolean;
  hasNextQuestion: boolean;
  selectedAnswerId?: number;
  correctAnswerId?: number;
  onSelectAnswer: (option: AnswerType) => void;
  onEndGame?: () => void;
  onSkipQuestion?: () => void;
  timerSlot?: ReactNode;
};

export const QuestionLayout = ({
  mode,
  question,
  currentIndex,
  questionCount,
  playerName,
  points,
  loadingNewQuestion,
  hasNextQuestion,
  selectedAnswerId,
  correctAnswerId,
  onSelectAnswer,
  onEndGame,
  onSkipQuestion,
  timerSlot,
}: QuestionLayoutProps) => {
  const { language } = useLanguage();

  const progressLabel = useMemo(() => {
    const current = currentIndex + 1;
    if (mode === 'survey') {
      return `${language.survey_question} ${current} ${language.of} ${questionCount}`;
    }
    return `${language.question} ${current} ${language.of} ${questionCount}`;
  }, [currentIndex, language, mode, questionCount]);

  const questionContent = useMemo(() => {
    if (loadingNewQuestion) {
      if (hasNextQuestion) {
        return (
          <NextQuestion>
            <CountdownWrapper className="fade-in">
              <CountdownBase size={80} />
            </CountdownWrapper>
            <h1 className="fade-in">
              {language.next_question}
              <br />
              {language['ready?']}
            </h1>
          </NextQuestion>
        );
      }

      return (
        <QuestionsCompleted>
          <h1 className="fade-in">{language.questions_completed}</h1>
          <h3 className="fade-in">{language.ready_to_see_your_results}</h3>
        </QuestionsCompleted>
      );
    }

    const content = question?.content;
    return (
      <QuestionText className={(content?.length || 0) >= 75 ? 'fade-in small-text' : 'fade-in'}>
        {content}
      </QuestionText>
    );
  }, [hasNextQuestion, language, loadingNewQuestion, question?.content]);

  return (
    <Container>
      <FloatingLogo />
      <TopBar>
        <ProgressLabel>{progressLabel}</ProgressLabel>
        <TimerSlot>{timerSlot}</TimerSlot>
      </TopBar>
      <RightColumn className={loadingNewQuestion ? 'fade-out' : 'fade-in'}>
        {questionContent}
        <Answers
          disabled={loadingNewQuestion}
          isLoading={loadingNewQuestion}
          options={question?.answers || []}
          selectedAnswerId={selectedAnswerId}
          correctAnswerId={correctAnswerId}
          onSelectAnswer={onSelectAnswer}
          hideCorrectAnswer={mode === 'survey'}
        />
      </RightColumn>
      <Bottom>
        <div>
          <p>{playerName}</p>
        </div>
        <div>
          <p>
            {points} {language.pts}.
          </p>
        </div>
        <div>
          {typeof onSkipQuestion === 'function' && (
            <SkipQuestionButton type="button" onClick={onSkipQuestion}>
              {language.skip_question}
            </SkipQuestionButton>
          )}
          {typeof onEndGame === 'function' && <a onClick={onEndGame}>{language.end_game}</a>}
        </div>
      </Bottom>
    </Container>
  );
};
